"use client"

import type { BlockchainFilter } from "@/app/blockchains/filters/blockchain-filters-scope"
import type { SpotlightDrop } from "@/app/page-main.reatom"
import type { LaunchedCollectionPublic } from "@anaconda/core/collection-launcher/types"
import { reatomComponent, useWrap } from "@reatom/react"
import { type ReactNode, useEffect, useMemo } from "react"
import {
  type CarouselLayouts,
  initializeSpotlightStore,
  serverLaunchesVersionAtom,
} from "./drops-spotlight-store.reatom"

interface Props {
  blockchain: BlockchainFilter
  drops?: SpotlightDrop[]
  launches?: LaunchedCollectionPublic[]
  /** How launcher admins arranged every page's carousel; undefined when unread. */
  layouts?: CarouselLayouts
  children: ReactNode
}

/**
 * Hydrates spotlight store with server data
 * Must be client component to call initialization
 */
export const DropsSpotlightHydrator = reatomComponent(
  ({ blockchain, drops, launches, layouts, children }: Props) => {
    // Initialize once per blockchain, drops, launched collections and layouts
    useMemo(() => {
      initializeSpotlightStore(blockchain, drops, launches, layouts)
    }, [blockchain, drops, launches, layouts])

    // A later render's launches and layouts (a client navigation back to
    // this chain) reach the store's computeds through the version atom
    const markLaunches = useWrap(() => {
      serverLaunchesVersionAtom.set((version) => version + 1)
    })
    // biome-ignore lint/correctness/useExhaustiveDependencies: blockchain, launches and layouts are what re-runs it
    useEffect(() => {
      markLaunches()
    }, [blockchain, launches, layouts, markLaunches])

    return <>{children}</>
  },
  "DropsSpotlightHydrator",
)
